import React from "react";
import { useTranslation } from "react-i18next";
import { FaFileAlt, FaSyncAlt, FaChalkboardTeacher, FaSearch, FaCubes, FaArrowRight } from "react-icons/fa";
import Seo from "../seo/Seo";
import CtaPair from "../components/CtaPair";
import { Link } from "../i18n/LangContext";
import { SITE_URL } from "../seo/site";
import "../styles/Offers.css";

// Paired positionally with offers.cards in the locale files.
const OFFER_ICONS = [<FaFileAlt />, <FaSyncAlt />, <FaChalkboardTeacher />];

const Offers = () => {
  const { t } = useTranslation();
  const cards = t("offers.cards", { returnObjects: true });
  const diagnostic = t("offers.diagnostic", { returnObjects: true });
  const custom = t("offers.custom", { returnObjects: true });
  const closing = t("offers.closing", { returnObjects: true });

  // An offer catalogue with prices is what lets an assistant answer
  // "what does AI automation cost in Vojvodina" with our numbers.
  const catalogJsonLd = {
    "@type": "OfferCatalog",
    "@id": `${SITE_URL}/offers#catalog`,
    name: t("offers.title"),
    itemListElement: [
      ...cards.map((card, i) => ({
        "@type": "Offer",
        position: i + 1,
        name: card.name,
        description: card.tagline,
        priceSpecification: {
          "@type": "PriceSpecification",
          price: card.key === "poc" ? "0" : card.key === "automation" ? "300" : "490",
          priceCurrency: "EUR",
          valueAddedTaxIncluded: false,
          ...(card.key === "automation" ? { billingDuration: "P1M" } : {}),
        },
        url: `${SITE_URL}${card.link}`,
      })),
      {
        "@type": "Offer",
        position: 4,
        name: diagnostic.title,
        description: diagnostic.priceNote,
        priceSpecification: {
          "@type": "PriceSpecification",
          minPrice: "2500",
          maxPrice: "3500",
          priceCurrency: "EUR",
          valueAddedTaxIncluded: false,
        },
      },
    ],
  };

  return (
    <div className="offers-page">
      <Seo page="offers" path="/offers" override={{ jsonLd: [catalogJsonLd] }} />

      <section className="offers-hero">
        <div className="container">
          <h1>{t("offers.title")}</h1>
          <p className="offers-lead">{t("offers.lead")}</p>
          <p className="offers-vat">{t("offers.vatNote")}</p>
          <CtaPair tone="dark" />
        </div>
      </section>

      <section className="offers-cards-section">
        <div className="container">
          <h2 className="offers-section-title">{t("offers.cardsTitle")}</h2>
          <div className="offers-cards">
            {cards.map((card, i) => (
              <article className="offer-card" key={card.key}>
                <span className="offer-card-icon" aria-hidden="true">{OFFER_ICONS[i]}</span>
                <h3 className="offer-card-name">{card.name}</h3>
                <p className="offer-card-tagline">{card.tagline}</p>
                <p className="offer-card-price">
                  <span className="offer-card-price-value">{card.price}</span>
                  <span className="offer-card-price-note">{card.priceNote}</span>
                </p>
                <p className="offer-card-for">{card.forWho}</p>
                <ul className="offer-card-points">
                  {card.points.map((point, j) => <li key={j}>{point}</li>)}
                </ul>
                <Link to={card.link} className="cta-inline offer-card-link">
                  {card.linkLabel} <FaArrowRight />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="offers-diagnostic">
        <div className="container">
          <span className="offer-eyebrow">{diagnostic.eyebrow}</span>
          <h2>{diagnostic.title}</h2>
          <div className="offer-price">
            <span className="offer-price-value"><FaSearch aria-hidden="true" /> {diagnostic.price}</span>
            <span className="offer-price-note">{diagnostic.priceNote}</span>
          </div>
          <p>{diagnostic.text}</p>
          <ul className="offers-points">
            {diagnostic.points.map((point, i) => <li key={i}>{point}</li>)}
          </ul>
        </div>
      </section>

      <section className="offers-custom">
        <div className="container">
          <h2><FaCubes aria-hidden="true" /> {custom.title}</h2>
          <p>{custom.text}</p>
          <p className="offers-custom-cta">{custom.cta}</p>
        </div>
      </section>

      <section className="offers-closing">
        <div className="container">
          <h2>{closing.title}</h2>
          <p>{closing.text}</p>
          <CtaPair tone="dark" align="center" />
        </div>
      </section>
    </div>
  );
};

export default Offers;
